<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
    </div>

    <div class="page-container">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <p class="error-message">{{ error }}</p>
        <button class="btn btn-secondary" @click="fetchData">重試</button>
      </div>

      <div v-else class="subscription-content">
        <div class="info-section">
          <h2>ChatDesk 訂閱狀態</h2>
          <div v-if="addonSubscription" class="subscription-status">
            <div class="status-item">
              <span class="status-label">訂閱狀態：</span>
              <span :class="getStatusBadge(addonSubscription.status)">{{ getStatusText(addonSubscription.status) }}</span>
            </div>
            <div class="status-item">
              <span class="status-label">計費週期：</span>
              <span class="status-value">{{ addonSubscription.billing_cycle === 'yearly' ? '年費' : '月費' }}</span>
            </div>
            <div class="status-item">
              <span class="status-label">每期金額：</span>
              <span class="status-value">NT$ {{ formatCurrency(addonSubscription.amount) }}</span>
            </div>
            <div v-if="addonSubscription.start_date" class="status-item">
              <span class="status-label">開始日期：</span>
              <span class="status-value">{{ formatDate(addonSubscription.start_date) }}</span>
            </div>
            <div v-if="addonSubscription.next_billing_date" class="status-item">
              <span class="status-label">下次扣款日：</span>
              <span class="status-value">{{ formatDate(addonSubscription.next_billing_date) }}</span>
            </div>
            <div class="status-item">
              <span class="status-label">座席：</span>
              <span class="status-value">
                {{ seatsSummary }}
              </span>
            </div>
          </div>
          <div v-else class="empty-state">
            <i class="ri-customer-service-2-line"></i>
            <p>尚未訂閱 ChatDesk 真人客服</p>
          </div>
        </div>

        <div v-if="!addonSubscription || addonSubscription.status === 'cancelled'" class="info-section">
          <h2>啟用 ChatDesk</h2>
          <div class="activation-box">
            <p>ChatDesk 提供 LINE 即時真人客服工作台：佇列、接手、回覆與會員情境面板。與 ChatMate AI 客服為獨立選購，可分開或併用。</p>
            <div class="billing-cycle-toggle">
              <button :class="['btn-toggle', billingCycle === 'monthly' ? 'active' : '']" @click="billingCycle = 'monthly'">
                月費 NT$ 1,000 / 月
              </button>
              <button :class="['btn-toggle', billingCycle === 'yearly' ? 'active' : '']" @click="billingCycle = 'yearly'">
                年費 NT$ 10,000 / 年
              </button>
            </div>
            <button class="btn btn-primary btn-lg" :disabled="activating" @click="handleActivate">
              <i class="ri-bank-card-line"></i>
              <span v-if="activating">處理中...</span>
              <span v-else>前往綠界付款啟用</span>
            </button>
            <p class="activate-hint">點擊後將跳轉至綠界付款頁面，完成首次扣款即可啟用</p>
          </div>
        </div>

        <div v-if="addonSubscription && addonSubscription.status === 'pending'" class="info-section">
          <h2>等待付款確認</h2>
          <div class="pending-box">
            <i class="ri-time-line"></i>
            <p>您的訂閱訂單已建立，等待付款確認中。</p>
            <div class="pending-actions">
              <button class="btn btn-secondary" @click="fetchData">重新整理狀態</button>
              <button class="btn btn-warning-outline" :disabled="cancelling" @click="handleCancelPendingAndRetry">
                <span v-if="cancelling">處理中...</span>
                <span v-else>取消，重新付款</span>
              </button>
              <button v-if="isDev" class="btn btn-dev" :disabled="devActivating" @click="handleDevActivate">
                <span v-if="devActivating">處理中...</span>
                <span v-else>模擬付款成功（本機測試）</span>
              </button>
            </div>
          </div>
        </div>

        <div v-if="addonSubscription && addonSubscription.status === 'active'" class="info-section">
          <h2>管理訂閱</h2>
          <div class="manage-section">
            <p class="manage-description">取消後將停止自動扣款，現有訂閱期限內仍可使用服務。</p>
            <router-link to="/client/chatdesk" class="btn btn-primary">前往工作台</router-link>
            <button class="btn btn-danger-outline" :disabled="cancelling" @click="handleCancel">
              <span v-if="cancelling">處理中...</span>
              <span v-else>取消 ChatDesk 訂閱</span>
            </button>
          </div>
        </div>
      </div>

      <form ref="ecpayForm" :action="ecpayPaymentUrl" method="POST" target="_self" style="display:none">
        <input v-for="(value, key) in ecpayOrderData" :key="key" type="hidden" :name="key" :value="value" />
      </form>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { showError, showSuccess, showConfirm } from '@/utils/swal.js';
import apiClient from '@/api/axios.js';
import { chatdeskService } from '@/api/modules/chatdesk.js';

export default {
  name: 'ChatDeskSubscription',
  setup() {
    const { pageTitle } = usePageTitle('ChatDesk 訂閱管理');
    const loading = ref(false);
    const error = ref(null);
    const addonSubscription = ref(null);
    const seats = ref(null);
    const billingCycle = ref('monthly');
    const activating = ref(false);
    const cancelling = ref(false);
    const isDev = process.env.NODE_ENV !== 'production';
    const devActivating = ref(false);
    const ecpayForm = ref(null);
    const ecpayPaymentUrl = ref('');
    const ecpayOrderData = reactive({});

    const seatsSummary = computed(() => {
      if (!seats.value) return '3 席（可另加購）';
      const { includedSeats, extraSeats, totalSeats } = seats.value;
      if (!extraSeats) return `${totalSeats || includedSeats || 3} 席（內含）`;
      return `${totalSeats} 席（內含 ${includedSeats}＋加購 ${extraSeats}）`;
    });

    const fetchData = async () => {
      loading.value = true;
      error.value = null;
      try {
        const res = await apiClient.get('/client/addon-subscriptions');
        if (res?.success && res?.data) {
          const subs = res.data.addonSubscriptions || [];
          addonSubscription.value = subs.find(s =>
            s.addon_type === 'chatdesk' && ['pending', 'active'].includes(s.status)
          ) || subs.find(s => s.addon_type === 'chatdesk') || null;
        }
        try {
          const seatRes = await chatdeskService.getSubscription();
          seats.value = seatRes?.data?.seats || null;
        } catch (_) {
          seats.value = null;
        }
      } catch (err) {
        error.value = err.response?.data?.message || err.message || '獲取訂閱狀態失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleActivate = async () => {
      activating.value = true;
      try {
        const res = await apiClient.post('/client/addon-subscriptions/chatdesk/initiate', {
          billingCycle: billingCycle.value
        });
        if (res?.success && res?.data) {
          const { paymentUrl, orderData, addonName, amount } = res.data;
          ecpayPaymentUrl.value = paymentUrl;
          Object.keys(ecpayOrderData).forEach(k => delete ecpayOrderData[k]);
          Object.assign(ecpayOrderData, orderData);
          await showSuccess(
            '準備付款',
            `即將跳轉至綠界付款頁面\n${addonName} NT$ ${amount.toLocaleString()} / ${billingCycle.value === 'yearly' ? '年' : '月'}`
          );
          setTimeout(() => ecpayForm.value?.submit(), 300);
        } else {
          await showError('啟用失敗', res?.message || '請稍後再試');
        }
      } catch (err) {
        await showError('啟用失敗', err.response?.data?.message || err.message || '啟用失敗');
      } finally {
        activating.value = false;
      }
    };

    const handleCancelPendingAndRetry = async () => {
      const confirmed = await showConfirm('取消並重新付款', '將取消目前的待付款訂單。確定嗎？');
      if (!confirmed) return;
      cancelling.value = true;
      try {
        await apiClient.post(`/client/addon-subscriptions/${addonSubscription.value.id}/cancel`, {});
        addonSubscription.value = null;
        await fetchData();
      } catch (err) {
        await showError('操作失敗', err.response?.data?.message || err.message);
      } finally {
        cancelling.value = false;
      }
    };

    const handleDevActivate = async () => {
      devActivating.value = true;
      try {
        await apiClient.post(`/client/addon-subscriptions/${addonSubscription.value.id}/dev-activate`);
        await showSuccess('[DEV] 模擬成功', '已模擬付款成功，訂閱已啟用');
        await fetchData();
      } catch (err) {
        await showError('失敗', err.response?.data?.message || err.message);
      } finally {
        devActivating.value = false;
      }
    };

    const handleCancel = async () => {
      const confirmed = await showConfirm('確認取消', '確定要取消 ChatDesk 訂閱嗎？');
      if (!confirmed) return;
      cancelling.value = true;
      try {
        await apiClient.post(`/client/addon-subscriptions/${addonSubscription.value.id}/cancel`, {});
        await showSuccess('已取消', 'ChatDesk 訂閱已取消');
        await fetchData();
      } catch (err) {
        await showError('取消失敗', err.response?.data?.message || err.message);
      } finally {
        cancelling.value = false;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('zh-TW');
    };
    const formatCurrency = (amount) => {
      if (!amount) return '0';
      return parseFloat(amount).toLocaleString('zh-TW', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    };
    const getStatusBadge = (status) => {
      const map = {
        pending: 'badge badge-warning',
        active: 'badge badge-success',
        cancelled: 'badge badge-secondary',
        expired: 'badge badge-danger'
      };
      return map[status] || 'badge badge-secondary';
    };
    const getStatusText = (status) => {
      const map = {
        pending: '待付款',
        active: '訂閱中',
        cancelled: '已取消',
        expired: '已過期'
      };
      return map[status] || status || '未知';
    };

    onMounted(fetchData);

    return {
      pageTitle,
      loading,
      error,
      addonSubscription,
      seatsSummary,
      billingCycle,
      activating,
      cancelling,
      ecpayForm,
      ecpayPaymentUrl,
      ecpayOrderData,
      isDev,
      devActivating,
      fetchData,
      handleActivate,
      handleDevActivate,
      handleCancelPendingAndRetry,
      handleCancel,
      formatDate,
      formatCurrency,
      getStatusBadge,
      getStatusText
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.subscription-content { display: flex; flex-direction: column; gap: 20px; }
.info-section {
  background: white; border-radius: 8px; padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}
.info-section h2 {
  font-size: 20px; font-weight: 600; margin-bottom: 16px; color: #333;
  border-bottom: 2px solid #e0e0e0; padding-bottom: 8px;
}
.subscription-status { display: flex; flex-direction: column; gap: 12px; }
.status-item { display: flex; align-items: center; gap: 12px; }
.status-label { font-weight: 600; color: #666; min-width: 120px; }
.status-value { color: #333; }
.activation-box p { color: #555; margin-bottom: 20px; line-height: 1.6; }
.billing-cycle-toggle {
  display: flex; gap: 0; margin-bottom: 20px; border: 1px solid #dee2e6;
  border-radius: 6px; overflow: hidden; width: fit-content;
}
.btn-toggle {
  padding: 10px 24px; border: none; background: white; cursor: pointer;
  font-size: 14px; color: #666;
}
.btn-toggle.active { background: #1976d2; color: white; font-weight: 600; }
.activate-hint { font-size: 13px; color: #888; margin-top: 12px; }
.pending-box {
  text-align: center; padding: 20px; background: #fff8e1;
  border: 1px solid #ffc107; border-radius: 8px;
}
.pending-actions {
  display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; margin-top: 12px;
}
.btn-warning-outline {
  background: transparent; color: #e65100; border: 1px solid #e65100;
  padding: 10px 20px; border-radius: 4px; cursor: pointer;
}
.btn-dev {
  background: #6a1b9a; color: white; border: none;
  padding: 10px 20px; border-radius: 4px; cursor: pointer;
}
.manage-section { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
.manage-description { color: #666; font-size: 14px; margin: 0; }
.empty-state { text-align: center; padding: 40px; color: #999; }
.empty-state i { font-size: 48px; margin-bottom: 16px; display: block; }
.btn {
  padding: 10px 20px; border: none; border-radius: 4px; font-size: 14px;
  cursor: pointer; display: inline-flex; align-items: center; gap: 8px;
  text-decoration: none;
}
.btn:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-lg { padding: 12px 28px; font-size: 15px; }
.btn-primary { background: #1976d2; color: white; }
.btn-secondary { background: #757575; color: white; }
.btn-danger-outline {
  background: transparent; color: #dc3545; border: 1px solid #dc3545;
}
.badge {
  display: inline-block; padding: 4px 10px; border-radius: 4px;
  font-size: 12px; font-weight: 500;
}
.badge-success { background: #e8f5e9; color: #2e7d32; }
.badge-warning { background: #fff8e1; color: #f57c00; }
.badge-danger { background: #ffebee; color: #c62828; }
.badge-secondary { background: #f5f5f5; color: #666; }
.loading-state, .error-state { text-align: center; padding: 40px; }
.spinner {
  border: 3px solid #f3f3f3; border-top: 3px solid #1976d2; border-radius: 50%;
  width: 40px; height: 40px; animation: spin 1s linear infinite; margin: 0 auto 16px;
}
@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
</style>
